/**
 * Chat (Workforce.ai) usage indicator. Mirrors the People credit-usage-indicator
 * capabilities with three display modes:
 * - button: a button + popover summary (compact, for nav/header use)
 * - inline: a compact inline box with progress bars
 * - detailed: a fuller, collapsible card (default, for the billing page)
 * - sidebar: a bordered icon button showing the plan name + a usage gauge
 *   (follows the left-nav plan-badge pattern), opening the same usage popover.
 *
 * Chat has two metered dimensions (snapshot + deep research queries), so each
 * mode renders one bar per dimension.
 */

import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import * as dayjs from 'dayjs';
import { Router } from '@angular/router';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { ChatCreditUsage, ChatCreditsService } from 'src/app/core/services/chat-credits.service';
import { AuthService } from 'src/app/auth/service/auth.service';
import { HostnameService } from 'src/app/hostname-router-service';
import { SubscriptionDialogComponent } from 'src/app/settings/billing/subscription-dialog/subscription-dialog.component';

interface ChatUsageMetric {
  label: string;
  used: number;
  limit: number | 'unlimited' | null;
}

@Component({
  selector: 'app-chat-usage-indicator',
  templateUrl: './chat-usage-indicator.component.html',
  styleUrls: ['./chat-usage-indicator.component.scss'],
  standalone: false,
})
export class ChatUsageIndicatorComponent implements OnInit, OnDestroy {
  @Input() displayMode: 'button' | 'inline' | 'detailed' | 'sidebar' = 'detailed';
  @Input() hideText: boolean = false;
  @Input() hideProgressBar: boolean = false;
  @Input() transparentBackground: boolean = false;
  // Optional explicit plan name; when omitted the plan is derived from the quotas.
  @Input() planName?: string;

  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;

  usage: ChatCreditUsage | null = null;
  isDetailCollapsed: boolean = false;
  private usageSub: Subscription;

  get metrics(): ChatUsageMetric[] {
    if (!this.usage) {
      return [];
    }
    return [
      {
        label: 'Snapshot queries',
        used: this.usage.snapshotUsed,
        limit: this.usage.snapshotLimit,
      },
      {
        label: 'Deep research queries',
        used: this.usage.deepResearchUsed,
        limit: this.usage.deepResearchLimit,
      },
    ];
  }

  // Worst-case percentage across the metered dimensions, for the button mini bar.
  get overallPercent(): number {
    const pcts = this.metrics
      .filter((m) => !this.isUnlimited(m.limit))
      .map((m) => this.percent(m.used, m.limit));
    return pcts.length ? Math.max(...pcts) : 0;
  }

  // At least one dimension has a real (numeric) quota to chart against.
  get hasMeteredQuota(): boolean {
    return this.metrics.some((m) => !this.isUnlimited(m.limit));
  }

  // Fewest queries left across the metered dimensions (null when all unlimited).
  // Drives a low-balance warning for small plans where 70% never trips (e.g.
  // Free's 3 deep-research queries).
  get lowestRemaining(): number | null {
    const remaining = this.metrics
      .filter((m) => !this.isUnlimited(m.limit))
      .map((m) => Math.max(0, (m.limit as number) - m.used));
    return remaining.length ? Math.min(...remaining) : null;
  }

  // Gauge fill: worst-case usage when metered, full ring when everything's unlimited.
  get gaugePercent(): number {
    return this.hasMeteredQuota ? this.overallPercent : 100;
  }

  // Plan name for the sidebar badge: explicit input, else the service-provided
  // plan (resolved authoritatively from the org's active subscriptions), else
  // Free. We never infer the plan from quota limits — an org with no chat
  // subscription is Free regardless of any custom limits in its settings.
  get planLabel(): string {
    return this.planName ?? this.usage?.planName ?? 'Free';
  }

  // Usage card lists deep research first, then snapshot, to match the design.
  get cardMetrics(): ChatUsageMetric[] {
    return [...this.metrics].reverse();
  }

  // Whole days until the usage cycle resets (>= 0); 0 means it resets today.
  get resetInDays(): number {
    if (!this.usage?.resetDate) {
      return 0;
    }
    return Math.max(0, dayjs(this.usage.resetDate).startOf('day').diff(dayjs().startOf('day'), 'day'));
  }

  // Next-tier upgrade CTA for the usage card; null when already on a top plan.
  get upgradeCtaLabel(): string | null {
    switch (this.planLabel?.toLowerCase()) {
      case 'free':
        return 'Upgrade to Pro';
      case 'pro':
        return 'Upgrade to Team';
      default:
        return null;
    }
  }

  constructor(
    private chatCreditsService: ChatCreditsService,
    private authService: AuthService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.usageSub = this.chatCreditsService
      .getChatCreditUsage()
      .subscribe((usage) => (this.usage = usage));
  }

  isUnlimited(limit: number | 'unlimited' | null): boolean {
    return limit === 'unlimited' || limit === null;
  }

  percent(used: number, limit: number | 'unlimited' | null): number {
    if (typeof limit !== 'number' || limit <= 0) {
      return 0;
    }
    return Math.min(100, Math.round((used / limit) * 100));
  }

  // Queries left for a single metric (null when unlimited), for per-bar warnings.
  metricRemaining(metric: ChatUsageMetric): number | null {
    return this.isUnlimited(metric.limit)
      ? null
      : Math.max(0, (metric.limit as number) - metric.used);
  }

  barColor(pct: number, remaining: number | null = null): string {
    switch (ChatCreditsService.usageSeverity(pct, remaining)) {
      case 'danger':
        return 'tw-bg-red-500';
      case 'warn':
        return 'tw-bg-yellow-500';
      default:
        return 'tw-bg-emerald-500';
    }
  }

  // Usage-card meter color: teal when healthy, amber as it fills / runs low, red near the cap.
  meterColor(pct: number, remaining: number | null = null): string {
    switch (ChatCreditsService.usageSeverity(pct, remaining)) {
      case 'danger':
        return 'tw-bg-red-500';
      case 'warn':
        return 'tw-bg-amber-400';
      default:
        return 'tw-bg-teal-400';
    }
  }

  // SVG stroke for the sidebar gauge (teal = unlimited, else threshold colors).
  gaugeStroke(): string {
    if (!this.hasMeteredQuota) {
      return '#14b8a6'; // teal-500
    }
    switch (ChatCreditsService.usageSeverity(this.overallPercent, this.lowestRemaining)) {
      case 'danger':
        return '#ef4444'; // red-500
      case 'warn':
        return '#eab308'; // yellow-500 (70%+ used, or <= 1 query left)
      default:
        return '#10b981'; // emerald-500
    }
  }

  toggleDetailCollapse(): void {
    this.isDetailCollapsed = !this.isDetailCollapsed;
  }

  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.menuTrigger?.menuOpen) {
      this.closeMenu();
    }
  }

  closeMenu(): void {
    if (this.menuTrigger && this.menuTrigger.menuOpen) {
      this.menuTrigger.closeMenu();
    }
  }

  // Open the chat plan picker in a popup rather than sending the user off to the
  // billing page. Falls back to navigating to the billing page if the dialog
  // can't be opened for any reason.
  goToBilling(): void {
    this.closeMenu();
    try {
      this.dialog.open(SubscriptionDialogComponent, {
        backdropClass: 'darker-dialog-backdrop',
        panelClass: 'subscription-dialog',
        data: { subscriptionType: 'chat' },
      });
    } catch {
      this.navigateToBillingPage();
    }
  }

  private navigateToBillingPage(): void {
    if (HostnameService.isWorkforceHostname()) {
      this.router.navigate(['/billing']);
    } else {
      this.router.navigate([`/${this.authService.getSelectedOrgIdValue}/settings`]);
    }
  }

  ngOnDestroy(): void {
    this.usageSub?.unsubscribe();
  }
}
